// driverService.js
// -----------------------------------------------------------------------------
// Every Firestore and Storage access for the driver flow lives here. The views
// must never touch `db` directly -- the same contract bookingService.js keeps for
// จองวันส่งสินค้า and dispatchService.js keeps for จัดรถอัตโนมัติ.
//
// All the judgement -- what a stop is, what order the day runs in, which stamp
// comes next -- is in driverDay.js, which is pure and covered by
// `npm run test:driver`. What lives here is reading the day, writing the result,
// and the three safety rules around that write:
//
//   1. A STAMP NEVER OVERWRITES A STAMP -- ENFORCED IN A TRANSACTION, not by the
//      client's copy of the stop. driverDay.canStamp is still run first, but only
//      as a fast local refusal: it reads the `steps` this device last loaded, and
//      a second device whose copy predates the first device's stamp would pass it
//      and then replace `at` / `by` / `byName` / `coords` outright (and append a
//      duplicate timeline entry as soon as the clock minute differed). So the
//      write runs inside db.runTransaction: the stop document is RE-READ there,
//      and if that step already carries an `at` the transaction throws
//      BAD_STEP_ORDER instead of writing anything. Firestore rules are open, so
//      this transaction is the only thing standing between two phones and a lost
//      arrival time.
//   2. THE LEGACY COLLECTIONS ARE MIRRORED IN THE SAME TRANSACTION. deliveryList
//      and deliveryTimeline are what track.vue, managecar.vue and the reports
//      read. They commit together with the stop or not at all, so a driver can be
//      moved between the old screen and the new one mid-day with nothing
//      stranded. A link whose document dispatch has since deleted is skipped, not
//      fatal -- see the note in stampStep.
//   3. ensureStops NEVER RE-ATTRIBUTES A STOP. It runs on every load and refresh
//      of day.vue and stop.vue, so the driver identity and the customer fields
//      are written ONLY when the document is being created. A relief driver
//      opening the day refreshes the plan, not whose name is on yesterday's work.
// -----------------------------------------------------------------------------

import { db, auth, functions, storage } from '@/firebase'
// `import * as`, matching src/firebase.js. The default export shape differs
// between firebase 6 builds; the namespace import is the one already proven to
// work in this project.
import * as firebase from 'firebase/app'
import moment from 'moment-timezone'
import D from '@/components/driverDay'
import tripLookup from '@/components/tripLookup'
import { reportWarning, reportError } from '@/components/report'
import S from '@/components/stationReport'
import P from '@/components/proofPhoto'
import O from '@/components/deliveryOutcome'
import C from '@/components/consentForm'

const STOP_COL = () => db.collection('deliveryStop')
const LIST_COL = () => db.collection('deliveryList')
const TIMELINE_COL = () => db.collection('deliveryTimeline')
const REPORT_COL = () => db.collection('stationReport')
const LOCATION_COL = () => db.collection('driverLocation')

// Same zone bookingDate.vue writes deliveryDate / bookingDay in, via
// bookingRules.js's TZ. new Date().toISOString() is UTC: between 00:00 and
// 07:00 Thailand time it names YESTERDAY, which would show an early-starting
// driver yesterday's jobs -- or an empty day.
const TZ = 'Asia/Bangkok'

// What the timeline says for each stamp. track.vue renders these to the
// customer, so the wording is theirs, not the driver's.
const TIMELINE_TEXT = {
  departed: { desc: 'กำลังเดินทางไปส่งสินค้า', icon: 'mdi-truck-fast-outline' },
  arrived: { desc: 'ถึงสถานที่ส่งสินค้า', icon: 'mdi-truck-outline' },
  installStarted: { desc: 'เริ่มติดตั้งสินค้า', icon: 'mdi-tools' },
  finished: { desc: 'จัดส่งสินค้าสำเร็จ', icon: 'mdi-truck-check-outline' },
  // What track.vue shows the customer. The reasons stay off it: they are for
  // the office, and the customer already knows why they refused.
  rejected: { desc: 'จัดส่งสินค้าไม่สำเร็จ', icon: 'mdi-truck-remove-outline' }
}

// --- position pings -----------------------------------------------------------
//
// THERE IS NO BACKGROUND GEOLOCATION IN A BROWSER. When the driver locks the
// phone or switches apps, mobile Chrome freezes timers and no position is read.
// A cadence measured in tens of minutes, expressed as a setInterval, would
// mostly never fire.
//
// So pings are OPPORTUNISTIC: every time the app becomes active -- resume,
// visibilitychange, a day refresh, any stamp -- we ask whether the last written
// fix is older than MIN_PING_AGE_MS, and only then take one. Drivers look at
// this app constantly while working, for the next address and the customer's
// phone number, so in practice this fires more reliably than any timer, and it
// costs nothing when they don't.
//
// There is deliberately NO movement filter. At ten minutes apart every fix is
// worth writing; a filter would be one more constant and one more thing to get
// wrong.
const MIN_PING_AGE_MS = 10 * 60 * 1000
const PING_BACKSTOP_MS = 5 * 60 * 1000
const PING_TIMEOUT_MS = 15000

let pingState = null

function readPosition () {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null)
    let settled = false
    let timer = null
    const done = value => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      resolve(value)
    }
    timer = setTimeout(() => done(null), PING_TIMEOUT_MS)
    navigator.geolocation.getCurrentPosition(
      pos => done({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => done(null),
      // maximumAge accepts a fix up to a minute old rather than forcing a fresh
      // GPS lock every time, which is where the battery actually goes.
      { enableHighAccuracy: true, timeout: PING_TIMEOUT_MS, maximumAge: 60000 }
    )
  })
}

/**
 * One position document per truck per day, overwritten. Deterministic id, the
 * same reasoning as deliveryStop's: two devices on one plate converge on one
 * document rather than racing to create duplicates.
 *
 * NEVER THROWS. A denied permission, a phone that cannot get a fix, or a
 * rejected write must never surface to a driver who is trying to do their job;
 * the board showing a stale truck is the correct and visible consequence.
 */
async function writePing (stop, coords, source) {
  try {
    const driver = await ensureDriverClaims()
    if (!driver || !stop || !stop.deliveryDate || !stop.regNum) return
    const point = coords || await readPosition()
    if (!point) return
    // Through safeIdPart, like every other id in this app: a '/' in a plate
    // would make set() throw into the swallow below, and that truck would
    // silently never have a position. fleetBoard builds its lookup key the same
    // way, so the two cannot diverge.
    const id = D.safeIdPart(stop.deliveryDate) + '_' + D.safeIdPart(stop.regNum)
    await LOCATION_COL().doc(id).set({
      deliveryDate: stop.deliveryDate,
      regNum: stop.regNum,
      driverUid: driver.uid,
      driverCode: driver.driverCode,
      driverName: driver.driverName,
      lat: point.lat,
      lng: point.lng,
      accuracy: point.accuracy == null ? null : point.accuracy,
      at: firebase.firestore.FieldValue.serverTimestamp(),
      atDevice: new Date().toISOString(),
      stopId: stop.id || '',
      source: source || 'ping'
    }, { merge: true })
    if (pingState) pingState.lastWriteAt = Date.now()
  } catch (e) {
    // Swallowed on purpose. See the doc comment.
  }
}

// The check the opportunistic triggers all run through. Cheap when it declines,
// which is most of the time.
function maybePing () {
  if (!pingState) return
  if (typeof document !== 'undefined' && document.hidden) return
  if (Date.now() - pingState.lastWriteAt < MIN_PING_AGE_MS) return
  writePing(pingState.stop, null, 'ping')
}

/**
 * Start pinging toward `stop`. Called when a `departed` stamp succeeds, and on
 * load when the day is already en route so a reload mid-leg resumes.
 *
 * Idempotent: re-arming for the same stop keeps the existing timer and the
 * existing age clock, so a driver refreshing day.vue does not force a fix every
 * time.
 */
function armPing (stop, coords) {
  if (!stop) return
  if (pingState && pingState.stop && pingState.stop.id === stop.id) {
    pingState.stop = stop
    return
  }
  disarmPing()
  pingState = { stop: stop, lastWriteAt: 0, timer: null, onVisible: null }
  pingState.onVisible = () => maybePing()
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', pingState.onVisible)
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', pingState.onVisible)
  }
  // The backstop only CHECKS the age; MIN_PING_AGE_MS decides whether anything
  // is written. It exists for the driver who leaves day.vue open on a mount and
  // never touches the phone.
  pingState.timer = setInterval(maybePing, PING_BACKSTOP_MS)
  // Ping #1 of the leg is the departed stamp's own coords -- already captured,
  // so the board is never blank for the first ten minutes of a leg.
  writePing(stop, coords || null, 'departed')
}

function disarmPing () {
  if (!pingState) return
  if (pingState.timer) clearInterval(pingState.timer)
  if (pingState.onVisible) {
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', pingState.onVisible)
    if (typeof window !== 'undefined') window.removeEventListener('focus', pingState.onVisible)
  }
  pingState = null
}

function isPinging () {
  return pingState != null
}

function todayISO () {
  return moment.tz(TZ).format('YYYY-MM-DD')
}

function nowClock () {
  return moment.tz(TZ).format('HH:mm')
}

// The single source of the plate a driver chose today, so carPick.vue
// (writer), day.vue and stop.vue (readers) cannot end up with two different
// validation rules for the same localStorage key. Owned here because
// signOutDriver already clears it.
//
// Keyed by date so yesterday's truck is never today's default. A stored
// value for any other date, or one that does not match the exact
// { date, regNum } shape -- an array, a bare string/number, a `regNum` that
// isn't a non-empty string -- counts as "no plate chosen" and must never
// throw: a stray localStorage value must never crash the view reading it.
const PLATE_KEY = 'driverPlate'

function storeChosenPlate (regNum) {
  localStorage.setItem(PLATE_KEY, JSON.stringify({ date: todayISO(), regNum: regNum }))
}

function readChosenPlate () {
  try {
    const saved = JSON.parse(localStorage.getItem(PLATE_KEY) || '{}')
    const valid = saved && typeof saved === 'object' && !Array.isArray(saved) &&
      saved.date === todayISO() && typeof saved.regNum === 'string' && saved.regNum
    if (valid) return saved.regNum
  } catch (e) { /* a corrupt value is the same as none */ }
  return ''
}

// The stable identifier for "auth has settled and there is genuinely no
// signed-in driver" -- thrown by every writer below that requires a driver.
// Callers (day.vue, and anywhere else that needs to send a signed-out driver
// back to the keypad instead of reporting a generic failure) must match on
// `err.code === NOT_SIGNED_IN`, never on the Thai message text: the message
// is free to be reworded for the driver reading it without silently breaking
// that check.
const NOT_SIGNED_IN = 'driver/not-signed-in'

function notSignedInError () {
  const err = new Error('ยังไม่ได้เข้าสู่ระบบ')
  err.code = NOT_SIGNED_IN
  return err
}

// Same pattern as NOT_SIGNED_IN above, for the two other Thai messages this
// module throws for a view to show verbatim: stampStep's out-of-order
// refusal, and submitStationReport's validation failure. A view must match
// `err.code`, never the message text.
const BAD_STEP_ORDER = 'driver/bad-step-order'
const INVALID_REPORT = 'driver/invalid-report'

// Raised in two places now -- the fast local check in stampStep, and the
// authoritative re-check inside the transaction -- so stop.vue sees one code and
// one message whichever of them refused the stamp.
function badStepOrderError () {
  const err = new Error('ลำดับการบันทึกเวลาไม่ถูกต้อง')
  err.code = BAD_STEP_ORDER
  return err
}

// Who is signed in, from the ID TOKEN CLAIMS rather than from storage.
//
// This matters more than it looks. Firebase Auth persists across an app restart
// (LOCAL persistence, IndexedDB), but sessionStorage does NOT survive closing an
// installed PWA. A driver who reopens the app the next morning is still signed
// in with an empty sessionStorage -- so a name read from there would be blank,
// and blank is exactly what gets written into `timeStampStartBy` and onto every
// stop document. The claims travel with the token and cannot go missing.
let claims = null

// Resolves once Firebase has restored (or confirmed there is no) session,
// exactly once for the life of the app. On a cold start of an installed PWA
// that restore is ASYNCHRONOUS -- auth.currentUser reads null for a beat even
// when the driver is in fact signed in. Reading it too early is what let
// ensureStops no-op silently instead of creating the day's stop documents,
// with the gap only surfacing later as a failed stamp at a customer's door.
// A timeout guards against onAuthStateChanged simply never firing; on timeout
// we resolve as "settled, no user" -- never as "proceed anonymously".
let authReadyPromise = null
const AUTH_READY_TIMEOUT_MS = 10000

function authReady () {
  if (!authReadyPromise) {
    authReadyPromise = new Promise(resolve => {
      let settled = false
      const timer = setTimeout(() => {
        if (settled) return
        settled = true
        unsubscribe()
        resolve()
      }, AUTH_READY_TIMEOUT_MS)
      const unsubscribe = auth.onAuthStateChanged(() => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        unsubscribe()
        resolve()
      })
    })
  }
  return authReadyPromise
}

// Exported (in addition to being used internally) so pin.vue can await the
// same auth-ready gate on mount and skip the keypad for an already-signed-in
// driver, instead of racing auth.currentUser itself.
async function ensureDriverClaims () {
  await authReady()
  const user = auth.currentUser
  if (!user) { claims = null; return null }
  if (claims && claims.uid === user.uid) return claims
  const result = await user.getIdTokenResult()
  claims = {
    uid: user.uid,
    driverCode: result.claims.driverCode || '',
    // THE TOKEN MANGLES THAI. firebase@6.6.2 decodes the payload byte-wise, so a
    // claim holding a Thai name arrives as its UTF-8 bytes, one per character --
    // and this is the value every deliveryStop, every stamp's `byName` and the
    // legacy timeStamp*By mirror are written from, which is how nine stops came
    // to carry à¸¨à¸¸à¸ instead of ศุภ. driverCode and status are ASCII and
    // survive, so this is the only field that needs it. See
    // scripts/repair-driver-names.js for the documents written before this.
    driverName: D.repairClaimText(result.claims.driverName || '')
  }
  // timestamp.vue still reads sessionStorage.driver and stays reachable as the
  // pilot fallback, so it is kept in step here rather than only at sign-in.
  sessionStorage.driver = claims.driverName
  sessionStorage.driverCode = claims.driverCode
  return claims
}

function currentDriver () {
  const user = auth.currentUser
  if (!user) return null
  if (claims && claims.uid === user.uid) return claims
  // Not hydrated yet — the uid is always right, and every writer below awaits
  // ensureDriverClaims() first, so this fallback is only ever a read.
  return { uid: user.uid, driverCode: '', driverName: '' }
}

async function signInWithPin (pin) {
  const call = functions.httpsCallable('driverSignIn')
  const result = await call({ pin: pin })
  const data = result.data
  await auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL)
  await auth.signInWithCustomToken(data.token)
  claims = null
  await ensureDriverClaims()
  return { driverCode: data.driverCode, driverName: data.driverName }
}

async function signOutDriver () {
  disarmPing()
  claims = null
  sessionStorage.removeItem('driver')
  sessionStorage.removeItem('driverCode')
  localStorage.removeItem(PLATE_KEY)
  await auth.signOut()
}

async function rowsFor (deliveryDate, regNum) {
  let query = LIST_COL().where('deliveryDate', '==', deliveryDate)
  if (regNum) query = query.where('regNum', '==', regNum)
  const snapshot = await query.get()
  const rows = []
  snapshot.forEach(doc => rows.push(Object.assign({ id: doc.id }, doc.data())))
  return rows
}

// A failed trip read must never break an ordinary day: it degrades to "no
// trips", so the driver sees today's own work as before.
async function openTripsSafe (date) {
  try { return await tripLookup.readOpenTrips(date) } catch (e) {
    reportWarning('trip lookup failed; showing today only', e)
    return []
  }
}

// The plates that actually have work today, with a stop count each, so a wrong
// tap on the car screen is obvious -- plus a plate on day 2+ of a multi-day
// trip, whose rows carry the departure day, not today.
async function loadPlates (deliveryDate) {
  const date = deliveryDate || todayISO()
  const [rows, trips] = await Promise.all([rowsFor(date, null), openTripsSafe(date)])
  const byPlate = {}
  rows.forEach(row => {
    const plate = row.regNum || ''
    if (!plate) return
    if (!byPlate[plate]) byPlate[plate] = []
    byPlate[plate].push(row)
  })
  const tripOf = {}
  for (const k of trips) {
    if (k.date === date) continue
    tripOf[k.regNum] = { dayNo: k.dayNo, days: k.days }
    ;(await rowsFor(k.date, k.regNum)).forEach(r => {
      if (!byPlate[k.regNum]) byPlate[k.regNum] = []
      byPlate[k.regNum].push(r)
    })
  }
  return Object.keys(byPlate).sort().map(plate => ({
    regNum: plate,
    stops: D.groupStopsByDate(byPlate[plate]).length,
    trip: tripOf[plate] || null
  }))
}

// deliveryTimeline is keyed by trackNo (the SO or RT number), not by the
// deliveryList document id, so the link between a stop's lines and their timeline
// documents has to be looked up. Done once per day load rather than per stamp.
async function timelineIdsFor (soNos) {
  const ids = {}
  // De-duplicated first so a repeated SO number (multiple lines on one stop)
  // does not waste a slot in the 10-value `in` chunks below.
  const unique = Array.from(new Set(soNos.filter(Boolean)))
  // Firestore caps an `in` query at 10 values, so the SO numbers are chunked.
  for (let i = 0; i < unique.length; i += 10) {
    const chunk = unique.slice(i, i + 10)
    if (!chunk.length) continue
    const snapshot = await TIMELINE_COL().where('trackNo', 'in', chunk).get()
    snapshot.forEach(doc => {
      const d = doc.data()
      if (d && d.trackNo) ids[String(d.trackNo)] = doc.id
    })
  }
  return ids
}

/**
 * The day, ready to render: grouped, ordered, ETA'd, and merged with whatever
 * has already been stamped -- including the earlier days of a multi-day trip
 * the plate is on.
 */
async function loadDay (regNum, deliveryDate) {
  const date = deliveryDate || todayISO()
  // A trip on day 2+ keeps the departure day on its rows (deliveryRow), so its
  // date is loaded beside today's. One (date, plate) pair per date.
  const trips = (await openTripsSafe(date)).filter(k => k.regNum === regNum && k.date !== date)
  const dates = [date].concat(trips.map(k => k.date))
  const rows = []
  for (const d of dates) (await rowsFor(d, regNum)).forEach(r => rows.push(r))

  const soNos = []
  rows.forEach(r => { const n = r.soNo || r.rtNo; if (n) soNos.push(String(n)) })
  const timelines = await timelineIdsFor(soNos)

  rows.forEach(r => {
    const n = String(r.soNo || r.rtNo || '')
    r.timelineId = timelines[n] || ''
  })

  const stops = D.groupStopsByDate(rows)

  const byId = {}
  for (const d of dates) {
    let storedQuery = STOP_COL().where('deliveryDate', '==', d)
    if (regNum) storedQuery = storedQuery.where('regNum', '==', regNum)
    const stored = await storedQuery.get()
    stored.forEach(doc => { byId[doc.id] = doc.data() })
  }

  stops.forEach(stop => {
    const saved = byId[stop.id] || {}
    stop.steps = saved.steps || {}
    stop.proof = saved.proof || null
    stop.sapDelivered = saved.sapDelivered || null
    stop.status = D.statusFor(stop.steps)
    stop.trip = D.tripInfo(stop, date)
  })

  // Planned AFTER the merge, never before. planStops re-anchors the ETA chain on
  // whatever has actually been stamped -- a stop finished at 11:15 is what the
  // rest of the day is predicted from -- so it has to be able to see `steps`.
  // Planning first (as this did) left every ETA chained from the depot at 08:00
  // for the whole day, which drifts further from the truth the later it gets.
  // groupStops assigns the ids the merge keys on, so the order is fixed:
  // group -> merge -> plan. A trip's stops are timed for today's work only
  // (planTripDay); ordinary jobs as always.
  const plain = stops.filter(s => !D.isTripStop(s))
  const trip = stops.filter(D.isTripStop)
  D.planStops(plain)
  D.planTripDay(trip, date)
  return plain.concat(trip)
}

/**
 * Creates the day's stop documents if they are not there yet, and keeps the
 * PLAN on the ones that already exist up to date.
 *
 * Deterministic ids plus merge is what makes this idempotent: two devices, or
 * the same driver reloading, converge on one document. `steps`, `proof` and
 * `status` are deliberately NOT written here -- only the plan fields and, on a
 * first write, the identity fields -- so calling this again can never blank a
 * stamp that has already been recorded.
 *
 * WHY THE SPLIT. This runs on EVERY load and refresh of day.vue and stop.vue.
 * Writing `driverUid` / `driverCode` / `driverName` on every one of those made
 * attribution last-writer-wins: a relief driver, or anyone whose truck had been
 * reassigned, rewrote the driver on stops another driver had already stamped
 * merely by OPENING the day. Per-stamp `by` / `byName` stayed correct, so the
 * record was recoverable -- but the document itself named the wrong person, and
 * that is what the reports read. The customer fields have the same shape of
 * problem, so they go the same way. Both are therefore written ONLY on the call
 * that creates the document.
 *
 * What still refreshes every time is the plan -- `seq`, `promisedTime`,
 * `installMinutes`, `location`, `lines` and the two query keys -- because
 * dispatch legitimately reorders a day, moves a promised time and corrects an
 * install estimate after the driver has already opened it.
 *
 * The existence read is not a transaction. Two devices creating the same stop
 * in the same instant can still both take the create branch, but they are both
 * creating a document that does not exist yet and both write the same plan; the
 * only thing at stake is whose identity lands, which is exactly the situation
 * before either of them stamped anything.
 *
 * Throws if nobody is signed in once auth has settled, rather than returning
 * silently -- a silent no-op here means the stop documents this visit needs
 * are never created, and the gap does not surface until a driver taps a stamp
 * hours later and the update() rejects with no stop to update.
 */
async function ensureStops (stops) {
  if (!stops || !stops.length) return
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()

  const refs = stops.map(stop => STOP_COL().doc(stop.id))
  const snaps = await Promise.all(refs.map(ref => ref.get()))

  const batch = db.batch()
  stops.forEach((stop, i) => {
    // Refreshed on every call: dispatch may have changed any of these since the
    // driver last looked.
    const plan = {
      deliveryDate: stop.deliveryDate,
      regNum: stop.regNum,
      seq: stop.seq,
      stopKey: stop.stopKey,
      lines: stop.lines,
      promisedTime: stop.promisedTime,
      installMinutes: stop.installMinutes,
      location: stop.location || null
    }
    // Written once, when the document is created, and never touched again.
    const identity = {
      driverUid: driver.uid,
      driverCode: driver.driverCode,
      driverName: driver.driverName,
      custName: stop.custName,
      custTel: stop.custTel,
      custAddress: stop.custAddress,
      cardCode: stop.cardCode,
      cardName: stop.cardName,
      cardPhone: stop.cardPhone
    }
    const payload = snaps[i].exists ? plan : Object.assign({}, identity, plan)
    batch.set(refs[i], payload, { merge: true })
  })
  await batch.commit()
}

/**
 * Records one stamp, and mirrors the two the legacy screens know about.
 *
 * `at` is a server timestamp: a phone with a wrong clock would otherwise poison
 * the delay analysis silently, with no way to detect it afterwards. `atDevice`
 * is the phone's own reading, kept beside it so the discrepancy is visible.
 *
 * `coords` is null when geolocation was denied or timed out. A driver standing
 * in a customer's living room must never be blocked because GPS did not lock.
 *
 * ATOMIC. The whole write runs inside one transaction, because the rule that a
 * stamp never overwrites a stamp cannot be enforced against the copy of the stop
 * this device happens to be holding. Two phones on one plate -- or one driver
 * whose second device still shows the day as he loaded it at 07:00 -- would both
 * pass driverDay.canStamp and the second would REPLACE `at` / `by` / `byName` /
 * `coords`, and append a second timeline entry to the customer's tracking page
 * as soon as the clock minute had ticked over. Firestore rules are open, so
 * there is no second line of defence behind this. The transaction re-reads the
 * stop and refuses -- with the same BAD_STEP_ORDER the local check raises -- if
 * the step already carries an `at`.
 *
 * A Firestore transaction requires ALL reads before ANY writes, so the stop and
 * every legacy link are read first, the decision is taken, and only then is
 * anything written.
 */
// `proof`, for the two stamps that close a stop (`finished` and `rejected`), is
// what the closing sheet just saved; O.listMirror picks from it the outcome
// fields copied onto deliveryList with the stamp.
async function stampStep (stop, step, coords, proof) {
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()
  // The fast local refusal: saves a round trip and keeps the button's own
  // feedback instant. It is NOT the guarantee -- the transaction below is.
  if (!D.canStamp(stop, step)) throw badStepOrderError()

  const clock = nowClock()

  // --- the legacy mirror ---
  const legacy = {}
  if (step === 'arrived') {
    legacy.timeStampStart = clock
    legacy.timeStampStartBy = driver.driverName
  }
  if (step === 'finished') {
    legacy.timeStampEnd = clock
    legacy.timeStampEndBy = driver.driverName
  }
  // A rejected visit writes no timeStampEnd: the legacy screens and the
  // delivery report read that field as "delivered", and this was not.
  Object.assign(legacy, O.listMirror(step, proof))

  const text = TIMELINE_TEXT[step]

  // FIVE STAFF DELETE SITES remove deliveryList and deliveryTimeline documents
  // when dispatch removes a job: managecar.vue's four confiDel* methods and
  // carBooking.vue's deleteBooking. All five now ask stampGuardForListDoc
  // first and warn before erasing stamped work, but they still delete -- the
  // guard informs, it does not refuse. If a job is removed after this driver
  // loaded the day, an update() against that now-missing id fails the ENTIRE
  // transaction -- the driver could not record any stamp on this visit until
  // reloading the app. So the linked documents are read inside the transaction,
  // and only the ones that still exist are written: a vanished link is skipped,
  // never fatal. Do NOT change these to set(..., { merge: true }) as a shortcut
  // past the read -- that would resurrect a delivery row dispatch deliberately
  // deleted, which is worse than skipping it.
  const candidates = []
  ;(stop.lines || []).forEach(line => {
    if (line.deliveryListId && Object.keys(legacy).length) {
      candidates.push({ ref: LIST_COL().doc(line.deliveryListId), data: legacy })
    }
    if (line.timelineId && text) {
      candidates.push({
        ref: TIMELINE_COL().doc(line.timelineId),
        data: {
          timeLine: firebase.firestore.FieldValue.arrayUnion({
            time: clock, desc: text.desc, icon: text.icon
          })
        }
      })
    }
  })

  const stopRef = STOP_COL().doc(stop.id)

  // Two lines of one visit can carry the same SO number and therefore the same
  // timelineId, so read each distinct document once: fewer round trips inside
  // the transaction, and no chance of the same ref being read twice.
  const linkRefs = []
  const linkPaths = []
  candidates.forEach(c => {
    if (linkPaths.indexOf(c.ref.path) === -1) {
      linkPaths.push(c.ref.path)
      linkRefs.push(c.ref)
    }
  })

  await db.runTransaction(async tx => {
    // ---- every read, before any write ----
    const stopSnap = await tx.get(stopRef)
    const exists = {}
    for (let i = 0; i < linkRefs.length; i++) {
      exists[linkPaths[i]] = (await tx.get(linkRefs[i])).exists
    }

    // ---- the decision, against what the SERVER holds ----
    const savedSteps = (stopSnap.exists && stopSnap.data() && stopSnap.data().steps) || {}
    // Already stamped by someone else: refuse rather than overwrite. Checked
    // explicitly as well as through canStamp, because this is the case the
    // whole transaction exists for.
    if (savedSteps[step] && savedSteps[step].at != null) throw badStepOrderError()
    // And the order has to hold against the server's steps too, not only
    // against the ones this device loaded.
    if (!D.canStamp({ steps: savedSteps }, step)) throw badStepOrderError()

    const stamp = {
      at: firebase.firestore.FieldValue.serverTimestamp(),
      atDevice: new Date().toISOString(),
      by: driver.uid,
      byName: driver.driverName,
      coords: coords || null
    }

    // ---- the writes ----
    const patch = {}
    patch['steps.' + step] = stamp
    patch.status = D.statusFor(Object.assign({}, savedSteps, { [step]: { at: stamp.atDevice } }))
    // A stop with sales orders owes SAP its delivered time; sapDelivered.js
    // pays it after this commits, and day load retries what is still owed.
    if (step === 'finished' && D.deliveredSos(stop).length) patch.sapDelivered = { pending: true }
    tx.update(stopRef, patch)

    candidates.forEach(c => {
      if (exists[c.ref.path]) tx.update(c.ref, c.data)
    })
  })
}

// A PNG data URL from the signature canvas -> Storage -> a download URL.
async function uploadSignature (stop, dataUrl) {
  const path = `driver-proof/${stop.deliveryDate}/${stop.id}/signature.png`
  const ref = storage.ref(path)
  await ref.putString(dataUrl, 'data_url')
  return ref.getDownloadURL()
}

// The installation consent's signature, beside the receipt signature but under
// its own fixed name, so neither can overwrite the other and a retry replaces
// rather than adds.
async function uploadConsentSignature (stop, dataUrl) {
  const path = `driver-proof/${stop.deliveryDate}/${stop.id}/consent-signature.png`
  const ref = storage.ref(path)
  await ref.putString(dataUrl, 'data_url')
  return ref.getDownloadURL()
}

// The consent the customer signed (or the ระบบขัดข้อง that stood in for it),
// saved BEFORE the เริ่มติดตั้ง photos and stamp, so installation never starts
// on record without it. Merged under `proof` like every other stage, so it
// touches no other proof field. consentForm.consentFields decides what is
// written; this adds the server's time and the driver who held the phone.
async function saveConsent (stop, input) {
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()
  const fields = Object.assign(C.consentFields(input), {
    consentAt: firebase.firestore.FieldValue.serverTimestamp(),
    consentBy: driver.uid
  })
  await STOP_COL().doc(stop.id).set({ proof: fields }, { merge: true })
}

// One stage's photos (proofPhoto.STAGES: the house on arrival, the room before
// installation, the finished work), already shrunk to JPEG blobs by
// driver/photoCapture.vue, -> Storage -> download URLs in the same order. One at
// a time rather than in parallel: a customer's home is often on one bar of
// signal, and five uploads
// racing each other there fail together more often than one after another.
// `onProgress(done, total)` lets the sheet say which photo it is on, because a
// spinner alone over a slow upload reads as a hang.
//
// The names are fixed (proofPhoto.storagePath), so a retry after a failure
// overwrites what the first attempt got through instead of adding copies.
//
// `start` is the first index to write, for photos added to a closed stop after
// the ones it already holds (proofPhoto.addableStages); 0 for a stage sheet.
async function uploadProofPhotos (stop, step, blobs, onProgress, start) {
  const list = blobs || []
  const first = Number(start) || 0
  const urls = []
  for (let i = 0; i < list.length; i++) {
    const path = P.storagePath(stop, step, first + i)
    // storage.rules refuses any name past the stage's limit; refusing here
    // first gives a real error instead of an opaque permission failure.
    if (!path) throw new Error(step + ' photo ' + (first + i + 1) + ' has no storage path')
    const ref = storage.ref(path)
    await ref.put(list[i], { contentType: 'image/jpeg' })
    urls.push(await ref.getDownloadURL())
    if (onProgress) onProgress(i + 1, list.length)
  }
  return urls
}

// The photos of the stamp about to be recorded -- ถึงบ้านลูกค้า or เริ่มติดตั้ง --
// or of a signed ใบส่งของ added to a stop already finished -- written BEFORE that stamp, so a stamp never exists without the photos the
// driver was asked for. `set` with merge writes only the stage's own two
// fields under `proof` (merge treats every nested leaf as its own field), so
// the house photo cannot blank the before photos, the signature, or the
// finish photos saved later -- and a retry simply writes the same fields again.
async function savePhotos (stop, step, urls, skippedReason) {
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()
  const fields = P.photoFields(step, urls, skippedReason)
  if (!Object.keys(fields).length) throw new Error(step + ' takes no photos')
  await STOP_COL().doc(stop.id).set({ proof: fields }, { merge: true })
}

// Photos the driver forgot, added to a closed stop. Read and written in one
// transaction, so two phones adding at once both keep their photos rather than
// the later list replacing the earlier; only the stage's list and `lateAdds`
// are written, so nothing else the visit saved is touched.
async function addLatePhotos (stop, step, urls) {
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()
  const stage = P.stageFor(step)
  if (!stage) throw new Error(step + ' takes no photos')
  const ref = STOP_COL().doc(stop.id)
  await db.runTransaction(async tx => {
    const snap = await tx.get(ref)
    const proof = (snap.exists && snap.data().proof) || {}
    const fields = P.lateAddFields(step, proof[stage.urlsField], urls, {
      prior: proof.lateAdds,
      at: new Date().toISOString(),
      by: driver.uid
    })
    tx.set(ref, { proof: fields }, { merge: true })
  })
}

// Page 2 of the finish sheet, saved when the CUSTOMER presses บันทึก: their
// signature, name, rating and comment, with the driver's photos of the work
// from page 1. No stamp -- the job closes on page 3. `capturedAt` is what tells
// a reopened sheet that the customer's part is done and it should open on
// page 3 rather than hand the phone back to someone who has already signed.
async function saveCustomerProof (stop, proof) {
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()
  await STOP_COL().doc(stop.id).set({
    proof: {
      signatureUrl: proof.signatureUrl || '',
      signedName: proof.signedName || '',
      rating: proof.rating || null,
      // A customer who chose not to rate, told apart from a rating nobody
      // asked for (null with this false).
      ratingDeclined: !proof.rating && !!proof.ratingDeclined,
      comment: proof.comment || '',
      refusedReason: proof.refusedReason || '',
      ...P.photoFields('finished', proof.photoUrls, proof.photoSkippedReason),
      capturedAt: firebase.firestore.FieldValue.serverTimestamp(),
      capturedBy: driver.uid
    }
  }, { merge: true })
}

// Page 3, the driver's: the signed ใบส่งของ and any follow-up the customer
// needs. Saved before the เสร็จงาน stamp, like every other photo. Returns the
// fields written, for stampStep's deliveryList mirror.
async function saveCloseout (stop, input) {
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()
  const fields = Object.assign(
    P.photoFields('receipt', input.receiptPhotoUrls, input.receiptSkippedReason),
    O.followUpFields(input)
  )
  await STOP_COL().doc(stop.id).set({ proof: fields }, { merge: true })
  return fields
}

// The ส่งไม่สำเร็จ sheet: reasons, note and evidence, saved before the
// `rejected` stamp so a failed stop never exists without its reasons. Returns
// the fields written, for stampStep's deliveryList mirror.
async function saveRejection (stop, input) {
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()
  const fields = Object.assign(O.rejectFields(input), {
    rejectedAt: firebase.firestore.FieldValue.serverTimestamp(),
    rejectedBy: driver.uid
  })
  await STOP_COL().doc(stop.id).set({ proof: fields }, { merge: true })
  return fields
}

/**
 * What a driver has already stamped for the visit one `deliveryList` row
 * belongs to -- asked by the STAFF screens that delete such a row, before they
 * open their confirm dialog.
 *
 * managecar.vue and carBooking.vue know the deliveryList document id and
 * nothing else about the stop, so the row is read here and the stop id derived
 * the one way it is ever derived: driverDay.stopIdFor over deliveryDate, regNum
 * and the stop key. That keeps `date_plate_stopKey` in a single place -- a
 * second copy of that formula in a view would drift from ensureStops and the
 * guard would silently start reading nothing.
 *
 * A stop spans every row of one customer visit, so deleting ONE line of a
 * multi-line visit warns about the whole visit. That is the truthful answer:
 * the stamps cover the visit, and one line of it is the part being erased.
 *
 * NEVER THROWS, and needs no driver session -- a staff user is signed in, not a
 * driver. A failed or unreadable read returns `{ known: false }`, and the
 * caller shows "could not check" and still allows the delete. Dispatch must
 * never be blocked from removing a job because this lookup had a bad minute;
 * the guard's whole job is to inform.
 */
async function stampGuardForListDoc (listDocId) {
  const unknown = { known: false, stamped: false, finished: false, status: 'pending', lastStep: null, lastAt: '' }
  if (!listDocId) return unknown
  try {
    const listSnap = await LIST_COL().doc(listDocId).get()
    if (!listSnap.exists) return Object.assign({}, unknown, { known: true })
    const row = listSnap.data() || {}
    const stopId = D.stopIdFor(row.deliveryDate, row.regNum, D.stopKeyFor(row))
    const stopSnap = await STOP_COL().doc(stopId).get()
    const guard = D.deleteGuard(stopSnap.exists ? stopSnap.data() : null)
    return Object.assign({ known: true }, guard)
  } catch (e) {
    return unknown
  }
}

async function submitStationReport (report) {
  const driver = await ensureDriverClaims()
  if (!driver) throw notSignedInError()
  const check = S.validateReport(report)
  if (!check.ok) {
    const err = new Error(check.errors.join('\n'))
    err.code = INVALID_REPORT
    throw err
  }
  const clean = S.normalizeReport(report)
  await REPORT_COL().add(Object.assign({}, clean, {
    reportedBy: driver.uid,
    reportedByName: driver.driverName,
    reportedAt: firebase.firestore.FieldValue.serverTimestamp(),
    status: 'new'
  }))
}

// Records that SAP took the delivered time. Touches only sapDelivered.
async function markSapDelivered (stop, { deliveredAt, sos }) {
  await db.collection('deliveryStop').doc(stop.id).update({
    sapDelivered: {
      pending: false,
      at: deliveredAt,
      sos,
      writtenAt: firebase.firestore.FieldValue.serverTimestamp()
    }
  })
}

// Gives up on an owed delivered time that is past D.sapDeliveredExpired's window,
// so the query below stops reading it. Best-effort: never throws.
async function expireSapDelivered (stop) {
  try {
    await db.collection('deliveryStop').doc(stop.id).update({
      sapDelivered: { pending: false, expired: true }
    })
  } catch (e) {
    reportWarning('expireSapDelivered failed for ' + stop.id + ':', e)
  }
}

// Stops THIS driver finished whose delivered time SAP still owes, whatever day
// they were -- the day list only loads today and running trips, so a stop
// finished yesterday in a dead zone would never be seen there. Equality filters
// only (no composite index), scoped to the finisher so a phone reads no other
// truck's customers. Looks back 3 days from each stop's FINISH, like the cron --
// not from deliveryDate, a trip's departure day. One older than that is marked
// expired, so it is not read again. Never throws.
async function loadOwedDelivered () {
  try {
    const user = auth.currentUser
    if (!user) return []
    const snap = await STOP_COL()
      .where('sapDelivered.pending', '==', true)
      .where('steps.finished.by', '==', user.uid)
      .get()
    const now = Date.now()
    const out = []
    const expired = []
    snap.forEach(doc => {
      const stop = Object.assign({ id: doc.id }, doc.data())
      if (D.sapDeliveredExpired(stop, now)) expired.push(stop)
      else out.push(stop)
    })
    for (const stop of expired) await expireSapDelivered(stop)
    return out
  } catch (e) {
    reportError('loadOwedDelivered failed:', e)
    return []
  }
}

export default {
  signInWithPin,
  signOutDriver,
  currentDriver,
  ensureDriverClaims,
  loadPlates,
  loadDay,
  ensureStops,
  stampStep,
  markSapDelivered,
  loadOwedDelivered,
  uploadSignature,
  uploadConsentSignature,
  saveConsent,
  uploadProofPhotos,
  savePhotos,
  addLatePhotos,
  saveCloseout,
  saveRejection,
  saveCustomerProof,
  submitStationReport,
  stampGuardForListDoc,
  todayISO,
  storeChosenPlate,
  readChosenPlate,
  armPing,
  disarmPing,
  isPinging,
  MIN_PING_AGE_MS,
  PING_BACKSTOP_MS,
  NOT_SIGNED_IN,
  BAD_STEP_ORDER,
  INVALID_REPORT
}
